import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  ArrowLeft,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Loader2,
  Play,
  Square,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  XCircle,
  Navigation,
  Key,
  PawPrint,
  FileText,
  Timer,
  Camera,
  Bot,
  CheckSquare,
  Circle,
  ImagePlus,
  X,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  DollarSign,
  Send,
  Car,
  Gauge,
} from 'lucide-react';
import { formatTime, formatDuration, cn } from '../lib/utils';
import { toast } from 'sonner';
import ImageLightbox from '../components/ImageLightbox';

const StaffJobHubPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showHelpDialog, setShowHelpDialog] = useState(false);
  const [showCantAttendDialog, setShowCantAttendDialog] = useState(false);
  const [showCompletionDialog, setShowCompletionDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showUpsellDialog, setShowUpsellDialog] = useState(false);
  const [showStaffNotesDialog, setShowStaffNotesDialog] = useState(false);
  const [newItemTask, setNewItemTask] = useState('');
  const [upsellNote, setUpsellNote] = useState('');
  const [staffNotes, setStaffNotes] = useState('');
  const [helpMessage, setHelpMessage] = useState('');
  const [cantAttendReason, setCantAttendReason] = useState('');
  
  // Timer state for on-site duration
  const [elapsedTime, setElapsedTime] = useState(0);
  
  // Checklist and photos state
  const [checklist, setChecklist] = useState([]);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [bookingPhotos, setBookingPhotos] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [uploadingItem, setUploadingItem] = useState(null);
  const [uploadingPhotoType, setUploadingPhotoType] = useState(null);
  
  // Refs for scrolling
  const checklistRef = useRef(null);
  const photosRef = useRef(null);
  const fileInputRef = useRef(null);
  const itemPhotoInputRef = useRef(null);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxSlides, setLightboxSlides] = useState([]);
  
  // Not Done reason dialog state
  const [showNotDoneDialog, setShowNotDoneDialog] = useState(false);
  const [notDoneItem, setNotDoneItem] = useState(null);
  const [notDoneReason, setNotDoneReason] = useState('');
  
  // Odometer tracking state
  const [showOdometerDialog, setShowOdometerDialog] = useState(false);
  const [odometerReading, setOdometerReading] = useState('');
  const [odometerType, setOdometerType] = useState(null); // 'arrive_job' or 'depart_job'
  const [lastOdometerReading, setLastOdometerReading] = useState(null);
  const [pendingOdometerAction, setPendingOdometerAction] = useState(null); // Function to call after odometer

  // Fetch last odometer reading
  const fetchLastOdometer = useCallback(async () => {
    try {
      const response = await api().get('/odometer/last');
      setLastOdometerReading(response.data.reading);
    } catch (error) {
      console.error('Failed to fetch last odometer:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchLastOdometer();
  }, [fetchLastOdometer]);

  const fetchBooking = useCallback(async () => {
    try {
      const response = await api().get(`/bookings/${id}`);
      setBooking(response.data);
      
      // Calculate initial elapsed time if on site
      if (response.data.checkin_time && response.data.status === 'in_progress') {
        const checkinDate = new Date(response.data.checkin_time);
        const now = new Date();
        setElapsedTime(Math.floor((now - checkinDate) / 1000));
      }
    } catch (error) {
      console.error('Failed to fetch booking:', error);
      toast.error('Failed to load job details');
      navigate('/my-day');
    } finally {
      setLoading(false);
    }
  }, [api, id, navigate]);

  const fetchChecklist = useCallback(async (preserveExpanded = false) => {
    try {
      const response = await api().get(`/bookings/${id}/checklist`);
      setChecklist(response.data.checklist || []);
      
      // Only set collapsed state on initial load, preserve user's expanded groups on refetch
      if (!preserveExpanded) {
        const groups = [...new Set(response.data.checklist?.map(item => item.group).filter(Boolean))];
        const collapsed = {};
        groups.forEach(g => collapsed[g] = false);
        setExpandedGroups(collapsed);
      }
    } catch (error) {
      console.error('Failed to fetch checklist:', error);
    }
  }, [api, id]);

  const fetchCompletionStatus = useCallback(async () => {
    try {
      const response = await api().get(`/bookings/${id}/completion-status`);
      setCompletionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch completion status:', error);
    }
  }, [api, id]);

  const fetchBookingPhotos = useCallback(async () => {
    try {
      const response = await api().get(`/bookings/${id}/photos`);
      setBookingPhotos(response.data || []);
    } catch (error) {
      console.error('Failed to fetch booking photos:', error);
    }
  }, [api, id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  useEffect(() => {
    if (booking) {
      fetchChecklist();
      fetchCompletionStatus();
      fetchBookingPhotos();
    }
  }, [booking, fetchChecklist, fetchCompletionStatus, fetchBookingPhotos]);

  // Timer effect for on-site jobs
  useEffect(() => {
    let interval;
    if (booking?.status === 'in_progress' && booking?.checkin_time) {
      interval = setInterval(() => {
        const checkinDate = new Date(booking.checkin_time);
        const now = new Date();
        setElapsedTime(Math.floor((now - checkinDate) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [booking?.status, booking?.checkin_time]);

  const formatElapsedTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${secs}s`;
  };

  const handleAccept = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/accept`);
      toast.success('Job accepted! You\'re all set.');
      fetchBooking();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleArrive = async () => {
    // First prompt for odometer reading
    setOdometerType('arrive_job');
    setOdometerReading('');
    setPendingOdometerAction(() => async () => {
      setActionLoading(true);
      try {
        await api().post(`/bookings/${id}/arrive`);
        toast.success('Arrival recorded! Have a great clean!');
        fetchBooking();
        fetchCompletionStatus();
        fetchLastOdometer();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record arrival');
      } finally {
        setActionLoading(false);
      }
    });
    setShowOdometerDialog(true);
  };

  const handleDepartClick = async () => {
    // First check completion status
    setActionLoading(true);
    try {
      const response = await api().get(`/bookings/${id}/completion-status`);
      setCompletionStatus(response.data);
      
      if (!response.data.can_complete) {
        // Show completion dialog with what's missing
        setShowCompletionDialog(true);
        setActionLoading(false);
      } else {
        setActionLoading(false);
        // Prompt for odometer reading before depart
        setOdometerType('depart_job');
        setOdometerReading('');
        setPendingOdometerAction(() => handleDepart);
        setShowOdometerDialog(true);
      }
    } catch (error) {
      toast.error('Failed to check completion status');
      setActionLoading(false);
    }
  };

  const handleDepart = async () => {
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/depart`);
      toast.success('Job completed! Great work! 🎉');
      setShowCompletionDialog(false);
      fetchLastOdometer();
      // Navigate to the booking detail page to show completion summary
      navigate(`/bookings/${id}?completed=true`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record departure');
    } finally {
      setActionLoading(false);
    }
  };

  const handleOdometerSubmit = async () => {
    if (!odometerReading || isNaN(parseFloat(odometerReading))) {
      toast.error('Please enter a valid odometer reading');
      return;
    }
    
    const reading = parseFloat(odometerReading);
    
    // Validate reading is higher than last
    if (lastOdometerReading && reading < lastOdometerReading.reading_km) {
      toast.error(`Reading must be higher than your last reading (${lastOdometerReading.reading_km} km)`);
      return;
    }
    
    setActionLoading(true);
    try {
      await api().post('/odometer/reading', {
        booking_id: id,
        reading_type: odometerType,
        reading_km: reading
      });
      
      // Calculate distance traveled if we have a previous reading
      if (lastOdometerReading) {
        const distance = reading - lastOdometerReading.reading_km;
        if (distance > 0) {
          toast.success(`Odometer recorded! Distance: ${distance.toFixed(1)} km`);
        } else {
          toast.success('Odometer reading recorded!');
        }
      } else {
        toast.success('Odometer reading recorded!');
      }
      
      setShowOdometerDialog(false);
      
      // Execute the pending action (arrive or depart)
      if (pendingOdometerAction) {
        await pendingOdometerAction();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record odometer');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSkipOdometer = async () => {
    setShowOdometerDialog(false);
    if (pendingOdometerAction) {
      await pendingOdometerAction();
    }
  };

  // Handle checklist item status change (Done / Not Done / Reset)
  const handleChecklistItemStatus = async (itemId, newStatus) => {
    const item = checklist.find(i => i.id === itemId);
    if (!item) return;
    
    const currentStatus = item.status || (item.completed ? 'done' : 'pending');
    
    // If clicking "Not Done", show dialog for reason
    if (newStatus === 'not_done') {
      setNotDoneItem(item);
      setNotDoneReason('');
      setShowNotDoneDialog(true);
      return;
    }
    
    // If clicking same status again (e.g., Done when already done), reset to pending
    if (newStatus === currentStatus) {
      try {
        await api().patch(`/bookings/${id}/checklist/${itemId}`, { status: 'pending' });
        fetchChecklist(true);  // Preserve expanded state
        fetchCompletionStatus();
        toast.success('Item reset to pending');
      } catch (error) {
        toast.error('Failed to update item');
      }
      return;
    }
    
    // Set status to done
    try {
      await api().patch(`/bookings/${id}/checklist/${itemId}`, { status: 'done' });
      fetchChecklist(true);  // Preserve expanded state
      fetchCompletionStatus();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };
  
  // Submit Not Done reason
  const handleSubmitNotDone = async () => {
    if (!notDoneItem || !notDoneReason.trim()) {
      toast.error('Please provide a reason');
      return;
    }
    
    try {
      await api().patch(`/bookings/${id}/checklist/${notDoneItem.id}`, { 
        status: 'not_done',
        not_done_reason: notDoneReason.trim()
      });
      setShowNotDoneDialog(false);
      setNotDoneItem(null);
      setNotDoneReason('');
      fetchChecklist(true);  // Preserve expanded state
      fetchCompletionStatus();
      toast.success('Item marked as not done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update item');
    }
  };

  const handleUploadBookingPhoto = async (photoType) => {
    setUploadingPhotoType(photoType);
    fileInputRef.current?.click();
  };

  const handlePhotoFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !uploadingPhotoType) return;
    
    setActionLoading(true);
    try {
      // Upload all selected files
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api().post(`/bookings/${id}/photos?photo_type=${uploadingPhotoType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      });
      
      await Promise.all(uploadPromises);
      
      const count = files.length;
      toast.success(`${count} ${uploadingPhotoType} photo${count > 1 ? 's' : ''} uploaded!`);
      await fetchBookingPhotos();
      await fetchCompletionStatus();
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setActionLoading(false);
      setUploadingPhotoType(null);
      if (e.target) e.target.value = '';
    }
  };

  const handleUploadItemPhoto = async (itemId) => {
    setUploadingItem(itemId);
    itemPhotoInputRef.current?.click();
  };

  const handleItemPhotoFileChange = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !uploadingItem) return;
    
    setActionLoading(true);
    try {
      // Upload all selected files for the checklist item
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api().post(`/bookings/${id}/checklist/${uploadingItem}/photos`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      });
      
      await Promise.all(uploadPromises);
      
      const count = files.length;
      toast.success(`${count} photo${count > 1 ? 's' : ''} uploaded!`);
      fetchChecklist(true);  // Preserve expanded state
      fetchCompletionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photos');
    } finally {
      setActionLoading(false);
      setUploadingItem(null);
      e.target.value = '';
    }
  };

  const scrollToChecklist = () => {
    setShowCompletionDialog(false);
    setTimeout(() => {
      checklistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const scrollToPhotos = () => {
    setShowCompletionDialog(false);
    setTimeout(() => {
      photosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleRequestHelp = async () => {
    if (helpMessage.length < 5) {
      toast.error('Please describe what you need help with');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/request-help?message=${encodeURIComponent(helpMessage)}`);
      toast.success('Help request sent to your manager');
      setShowHelpDialog(false);
      setHelpMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send help request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCantAttend = async () => {
    if (cantAttendReason.length < 10) {
      toast.error('Please provide a detailed reason');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/cant-attend?reason=${encodeURIComponent(cantAttendReason)}`);
      toast.success('Managers notified. Job returned for reassignment.');
      setShowCantAttendDialog(false);
      navigate('/my-day');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to report');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddChecklistItem = async () => {
    if (newItemTask.length < 3) {
      toast.error('Please enter a task description');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/checklist`, {
        task: newItemTask,
        group: 'Additional Requests',
        required: true,
        photo_required: false
      });
      toast.success('Task added to checklist!');
      setShowAddItemDialog(false);
      setNewItemTask('');
      await fetchChecklist(true);  // Preserve expanded state
      await fetchCompletionStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSendUpsellNote = async () => {
    if (upsellNote.length < 10) {
      toast.error('Please describe the additional service opportunity');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/upsell-note?note=${encodeURIComponent(upsellNote)}`);
      toast.success('Note sent to Taleeia! She\'ll contact the client.');
      setShowUpsellDialog(false);
      setUpsellNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveStaffNotes = async () => {
    if (!staffNotes.trim()) {
      toast.error('Please enter your notes');
      return;
    }
    setActionLoading(true);
    try {
      await api().post(`/bookings/${id}/staff-notes?notes=${encodeURIComponent(staffNotes)}`);
      toast.success('Notes saved and sent to management!');
      setShowStaffNotesDialog(false);
      // Update local booking state to reflect notes
      setBooking(prev => ({ ...prev, staff_notes: staffNotes }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    } finally {
      setActionLoading(false);
    }
  };

  const openInMaps = () => {
    const address = encodeURIComponent(booking.service_address);
    window.open(`https://www.google.com/maps/search/?api=1&query=${address}`, '_blank');
  };

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Open lightbox with photos
  const openLightbox = (photos, index = 0) => {
    const slides = photos.map((photo) => ({
      src: photo.fullUrl || `${process.env.REACT_APP_BACKEND_URL}${photo.url}`,
      alt: photo.photo_type || 'Photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Open lightbox for checklist item photos
  const openItemLightbox = (item, index = 0) => {
    const slides = item.photos.map((photo) => ({
      src: `${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}/checklist/${item.id}/photos/${photo.filename}/file`,
      alt: item.task || 'Item photo',
    }));
    setLightboxSlides(slides);
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  // Group checklist items
  const groupedChecklist = checklist.reduce((acc, item) => {
    const group = item.group || 'General';
    if (!acc[group]) acc[group] = [];
    acc[group].push(item);
    return acc;
  }, {});

  // Count photos by type
  const photosByType = bookingPhotos.reduce((acc, photo) => {
    const type = photo.photo_type || 'other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(photo);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!booking) {
    return null;
  }

  const isAssignedToMe = booking.assigned_staff_id === user?.id;
  const isManagerOrDirector = user?.role === 'director' || user?.role === 'manager';
  const canAccept = booking.status === 'assigned' && isAssignedToMe;
  const canArrive = booking.status === 'accepted' && isAssignedToMe;
  const isOnSite = booking.status === 'in_progress' && isAssignedToMe;
  const isJobInProgress = booking.status === 'in_progress';
  const canViewJobDetails = isOnSite || (isJobInProgress && isManagerOrDirector);
  const isCompleted = booking.status === 'completed';
  const hasChecklist = checklist.length > 0;

  // Calculate checklist progress
  const requiredItems = checklist.filter(i => i.required !== false);
  const completedRequired = requiredItems.filter(i => i.completed);
  const checklistProgress = requiredItems.length > 0 
    ? Math.round((completedRequired.length / requiredItems.length) * 100) 
    : 100;

  return (
    <div className="space-y-4 pb-32 md:pb-6">
      {/* Hidden file inputs - multiple selection enabled, no capture restriction */}
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        multiple
        onChange={handlePhotoFileChange}
        className="hidden"
      />
      <input
        type="file"
        ref={itemPhotoInputRef}
        accept="image/*"
        multiple
        onChange={handleItemPhotoFileChange}
        className="hidden"
      />

      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/my-day')}
          data-testid="back-button"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-xl font-semibold text-zinc-900">{booking.client?.name || 'Job Details'}</h1>
          <p className="text-sm text-zinc-500">
            {booking.time_window_start && `${booking.time_window_start} - ${booking.time_window_end || 'TBC'}`}
          </p>
        </div>
      </div>

      {/* Things to Note Warning - Important Client Info */}
      {booking.client?.things_to_note && (
        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4" data-testid="things-to-note-alert">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-red-800 text-lg">Things to Note</p>
              <p className="text-red-700 mt-1 whitespace-pre-wrap">{booking.client.things_to_note}</p>
            </div>
          </div>
        </div>
      )}

      {/* On Site Banner */}
      {isOnSite && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
              <Timer className="h-6 w-6 text-amber-600 animate-pulse" />
            </div>
            <div>
              <p className="font-semibold text-amber-800">On Site</p>
              <p className="text-2xl font-bold text-amber-700">{formatElapsedTime(elapsedTime)}</p>
            </div>
          </div>
          <p className="text-sm text-amber-600">
            Arrived {formatTime(booking.checkin_time)}
          </p>
        </div>
      )}

      {/* Completed Banner */}
      {isCompleted && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
          <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          </div>
          <div>
            <p className="font-semibold text-emerald-800">Completed</p>
            <p className="text-sm text-emerald-600">
              {booking.total_time_on_site_minutes && formatDuration(booking.total_time_on_site_minutes)}
              {booking.checkin_time && ` • ${formatTime(booking.checkin_time)} - ${formatTime(booking.checkout_time)}`}
            </p>
          </div>
        </div>
      )}

      {/* Address Card with Map Link */}
      <Card className="border-zinc-100">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-zinc-100 flex items-center justify-center flex-shrink-0">
              <MapPin className="h-5 w-5 text-zinc-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-900">{booking.service_address}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={openInMaps}
                data-testid="open-maps-btn"
              >
                <Navigation className="h-4 w-4 mr-2" />
                Open in Maps
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Client Contact Card */}
      {booking.client && (
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-zinc-500" />
              Client Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="font-medium text-zinc-900">{booking.client.name}</p>
            {booking.client.phone && (
              <a
                href={`tel:${booking.client.phone}`}
                className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
                data-testid="call-client-btn"
              >
                <Phone className="h-4 w-4" />
                {booking.client.phone}
              </a>
            )}
            {booking.client.email && (
              <a
                href={`mailto:${booking.client.email}`}
                className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900"
              >
                <Mail className="h-4 w-4" />
                {booking.client.email}
              </a>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job Scope Card */}
      {booking.job_details && (
        <Card className="border-zinc-100">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4 text-zinc-500" />
              Job Scope
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-zinc-700 whitespace-pre-wrap">{booking.job_details}</p>
          </CardContent>
        </Card>
      )}

      {/* Access Notes Card */}
      {(booking.access_notes || booking.client?.access_instructions || booking.client?.pets) && (
        <Card className="border-zinc-100 border-amber-200 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2 text-amber-800">
              <Key className="h-4 w-4" />
              Access Notes
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(booking.access_notes || booking.client?.access_instructions) && (
              <p className="text-zinc-700">
                {booking.access_notes || booking.client?.access_instructions}
              </p>
            )}
            {booking.client?.pets && (
              <div className="flex items-start gap-2 text-amber-700">
                <PawPrint className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p><strong>Pets:</strong> {booking.client.pets}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* CHECKLIST SECTION */}
      {canViewJobDetails && (
        <div ref={checklistRef}>
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <CheckSquare className="h-4 w-4 text-zinc-500" />
                  Job Checklist
                  {booking.package_name_snapshot && (
                    <Badge variant="outline" className="ml-2 font-normal">
                      {booking.package_name_snapshot}
                    </Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-zinc-500">
                    {completedRequired.length}/{requiredItems.length}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddItemDialog(true)}
                    data-testid="add-checklist-item-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Task
                  </Button>
                </div>
              </div>
              {hasChecklist && (
                <Progress value={checklistProgress} className="h-2 mt-2" />
              )}
            </CardHeader>
            <CardContent className="space-y-2">
              {!hasChecklist ? (
                <div className="py-4 text-center space-y-3">
                  <p className="text-sm text-zinc-500 italic">
                    No checklist for this booking
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowAddItemDialog(true)}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add First Task
                  </Button>
                </div>
              ) : (
                Object.entries(groupedChecklist).map(([group, items]) => (
                  <div key={group} className="border rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleGroup(group)}
                      className="w-full flex items-center justify-between p-3 bg-zinc-50 hover:bg-zinc-100 transition-colors"
                    >
                      <span className="font-medium text-zinc-700">{group}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-zinc-500">
                          {items.filter(i => i.completed || i.status === 'done' || i.status === 'not_done').length}/{items.length}
                        </span>
                        {expandedGroups[group] ? (
                          <ChevronUp className="h-4 w-4 text-zinc-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-zinc-400" />
                        )}
                      </div>
                    </button>
                    {expandedGroups[group] && (
                      <div className="divide-y">
                        {items.map((item) => {
                          const itemStatus = item.status || (item.completed ? 'done' : 'pending');
                          return (
                          <div
                            key={item.id}
                            className={cn(
                              "p-3",
                              itemStatus === 'done' && "bg-emerald-50/50",
                              itemStatus === 'not_done' && "bg-amber-50/50"
                            )}
                          >
                            {/* Item Row */}
                            <div className="flex items-start gap-3">
                              {/* Status Indicator */}
                              <div className="mt-0.5 flex-shrink-0">
                                {itemStatus === 'done' && (
                                  <div className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                  </div>
                                )}
                                {itemStatus === 'not_done' && (
                                  <div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center">
                                    <XCircle className="h-3.5 w-3.5" />
                                  </div>
                                )}
                                {itemStatus === 'pending' && (
                                  <Circle className="h-5 w-5 text-zinc-300" />
                                )}
                              </div>
                              
                              {/* Task Content */}
                              <div className="flex-1 min-w-0">
                                <p className={cn(
                                  "text-sm",
                                  itemStatus === 'done' && "text-zinc-500 line-through",
                                  itemStatus === 'not_done' && "text-amber-700"
                                )}>
                                  {item.task}
                                </p>
                                
                                {/* Status Buttons */}
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    onClick={() => handleChecklistItemStatus(item.id, 'done')}
                                    className={cn(
                                      "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                                      itemStatus === 'done' 
                                        ? "bg-emerald-500 text-white" 
                                        : "bg-zinc-100 text-zinc-600 hover:bg-emerald-100 hover:text-emerald-700"
                                    )}
                                    data-testid={`done-btn-${item.id}`}
                                  >
                                    Done
                                  </button>
                                  <button
                                    onClick={() => handleChecklistItemStatus(item.id, 'not_done')}
                                    className={cn(
                                      "px-3 py-1.5 rounded-full text-xs font-medium transition-colors",
                                      itemStatus === 'not_done' 
                                        ? "bg-amber-500 text-white" 
                                        : "bg-zinc-100 text-zinc-600 hover:bg-amber-100 hover:text-amber-700"
                                    )}
                                    data-testid={`not-done-btn-${item.id}`}
                                  >
                                    Not Done
                                  </button>
                                </div>
                                
                                {/* Badges */}
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {item.required !== false && itemStatus === 'pending' && (
                                    <Badge variant="secondary" className="text-xs">Required</Badge>
                                  )}
                                  {item.photo_required && (
                                    <Badge 
                                      variant={item.photos?.length > 0 ? "default" : "destructive"} 
                                      className="text-xs"
                                    >
                                      <Camera className="h-3 w-3 mr-1" />
                                      Photo {item.photos?.length > 0 ? `(${item.photos.length})` : 'Required'}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Photo upload section */}
                                {item.photo_required && (
                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {item.photos?.map((photo, idx) => (
                                      <div 
                                        key={idx} 
                                        className="h-16 w-16 rounded bg-zinc-100 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => openItemLightbox(item, idx)}
                                        data-testid={`item-photo-${item.id}-${idx}`}
                                      >
                                        <img
                                          src={`${process.env.REACT_APP_BACKEND_URL}/api/bookings/${id}/checklist/${item.id}/photos/${photo.thumbnail}/file`}
                                          alt="Item"
                                          className="h-full w-full object-cover"
                                        />
                                      </div>
                                    ))}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-12 w-12 p-0"
                                      onClick={() => handleUploadItemPhoto(item.id)}
                                      disabled={actionLoading}
                                      data-testid={`upload-item-photo-${item.id}`}
                                    >
                                      {uploadingItem === item.id ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <ImagePlus className="h-4 w-4" />
                                      )}
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Not Done Reason Display */}
                            {itemStatus === 'not_done' && item.not_done_reason && (
                              <div className="mt-2 ml-8 px-3 py-2 bg-amber-100/50 rounded border border-amber-200">
                                <p className="text-xs text-amber-700">
                                  <span className="font-medium">Reason:</span> {item.not_done_reason}
                                </p>
                              </div>
                            )}
                          </div>
                        )})}
                      </div>
                    )}
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* PHOTOS SECTION */}
      {canViewJobDetails && (
        <div ref={photosRef}>
          <Card className="border-zinc-100">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Camera className="h-4 w-4 text-zinc-500" />
                Job Photos
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Photo category buttons */}
              <div className="grid grid-cols-2 gap-3">
                {['before', 'during', 'after', 'issue'].map((type) => {
                  const count = photosByType[type]?.length || 0;
                  const isAfter = type === 'after';
                  const hasAfterPhoto = count > 0;
                  
                  return (
                    <div key={type} className="space-y-2">
                      <Button
                        variant={isAfter && !hasAfterPhoto ? "destructive" : "outline"}
                        className={cn(
                          "w-full justify-start",
                          isAfter && !hasAfterPhoto && "animate-pulse"
                        )}
                        onClick={() => handleUploadBookingPhoto(type)}
                        disabled={actionLoading}
                        data-testid={`upload-${type}-photo-btn`}
                      >
                        {uploadingPhotoType === type ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Camera className="h-4 w-4 mr-2" />
                        )}
                        <span className="capitalize">{type}</span>
                        {count > 0 && (
                          <Badge variant="secondary" className="ml-auto">{count}</Badge>
                        )}
                      </Button>
                      {isAfter && !hasAfterPhoto && (
                        <p className="text-xs text-red-600 font-medium px-1">
                          Required before departure
                        </p>
                      )}
                      {/* Thumbnails */}
                      {count > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {photosByType[type].slice(0, 4).map((photo, idx) => (
                            <div 
                              key={photo.id} 
                              className="h-14 w-14 rounded bg-zinc-100 overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                              onClick={() => openLightbox(photosByType[type], idx)}
                              data-testid={`photo-${type}-${idx}`}
                            >
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                                alt={type}
                                className="h-full w-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          ))}
                          {count > 4 && (
                            <div 
                              className="h-14 w-14 rounded bg-zinc-200 flex items-center justify-center text-xs text-zinc-600 cursor-pointer hover:bg-zinc-300 transition-colors"
                              onClick={() => openLightbox(photosByType[type], 4)}
                            >
                              +{count - 4}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Help & Can't Attend Buttons */}
      {!isCompleted && isAssignedToMe && (
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => setShowHelpDialog(true)}
            data-testid="request-help-btn"
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Request Help
          </Button>
          <Button
            variant="outline"
            className="flex-1 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => setShowCantAttendDialog(true)}
            data-testid="cant-attend-btn"
          >
            <XCircle className="h-4 w-4 mr-2" />
            Can't Attend
          </Button>
        </div>
      )}

      {/* Note for Taleeia - Upsell Opportunity */}
      {isOnSite && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="font-medium text-emerald-900">Extra service needed?</p>
                  <p className="text-xs text-emerald-700">Alert Taleeia to contact client</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                onClick={() => setShowUpsellDialog(true)}
                data-testid="upsell-note-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Alert Taleeia
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ask Taco with Photo - Quick access */}
      {isAssignedToMe && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-medium text-amber-900">Need cleaning advice?</p>
                  <p className="text-xs text-amber-700">Snap a photo for Taco's guidance</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => navigate(`/assistant?job_id=${id}`)}
                data-testid="ask-taco-btn"
              >
                <Camera className="h-4 w-4 mr-2" />
                Ask Taco
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Staff Notes for Management */}
      {isOnSite && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-blue-900">Job Notes</p>
                  <p className="text-xs text-blue-700">Add notes for management about this job</p>
                </div>
              </div>
              <Button
                size="sm"
                className="bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => {
                  setStaffNotes(booking?.staff_notes || '');
                  setShowStaffNotesDialog(true);
                }}
                data-testid="staff-notes-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                {booking?.staff_notes ? 'Edit Notes' : 'Add Notes'}
              </Button>
            </div>
            {booking?.staff_notes && (
              <div className="mt-3 p-3 bg-blue-100 rounded-lg">
                <p className="text-sm text-blue-800">{booking.staff_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Fixed Bottom Action Button */}
      {isAssignedToMe && !isCompleted && (
        <div className="fixed bottom-20 md:bottom-6 left-0 right-0 px-4 md:pl-72 md:pr-8">
          <div className="max-w-2xl mx-auto">
            {canAccept && (
              <Button
                className="w-full h-16 text-lg bg-zinc-900 hover:bg-zinc-800 rounded-xl shadow-lg"
                onClick={handleAccept}
                disabled={actionLoading}
                data-testid="accept-job-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="h-6 w-6 mr-3" />
                    Accept Job
                  </>
                )}
              </Button>
            )}
            
            {canArrive && (
              <Button
                className="w-full h-16 text-lg bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg"
                onClick={handleArrive}
                disabled={actionLoading}
                data-testid="arrive-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Play className="h-6 w-6 mr-3" />
                    Arrive - I'm Here
                  </>
                )}
              </Button>
            )}
            
            {isOnSite && (
              <Button
                className="w-full h-16 text-lg bg-amber-500 hover:bg-amber-600 rounded-xl shadow-lg"
                onClick={handleDepartClick}
                disabled={actionLoading}
                data-testid="depart-btn"
              >
                {actionLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin" />
                ) : (
                  <>
                    <Square className="h-6 w-6 mr-3" />
                    Depart - Job Done
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* COMPLETION STATUS DIALOG */}
      <Dialog open={showCompletionDialog} onOpenChange={setShowCompletionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="h-5 w-5" />
              Almost Done!
            </DialogTitle>
            <DialogDescription>
              Please complete these items before departing:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {/* Arrival Status */}
            <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
              <div className="flex items-center gap-2">
                {completionStatus?.timestamps?.has_arrival ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span>Arrived on site</span>
              </div>
              {completionStatus?.timestamps?.has_arrival ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">Done</Badge>
              ) : (
                <Badge variant="destructive">Missing</Badge>
              )}
            </div>

            {/* After Photo Status */}
            <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
              <div className="flex items-center gap-2">
                {completionStatus?.photos?.is_complete ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-500" />
                )}
                <span>After photo</span>
              </div>
              {completionStatus?.photos?.is_complete ? (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                  {completionStatus.photos.after_count} uploaded
                </Badge>
              ) : (
                <Button size="sm" variant="destructive" onClick={() => {
                  scrollToPhotos();
                  setTimeout(() => handleUploadBookingPhoto('after'), 200);
                }}>
                  <Camera className="h-4 w-4 mr-1" />
                  Add Photo
                </Button>
              )}
            </div>

            {/* Checklist Status */}
            {completionStatus?.checklist?.require_checklist_completion && (
              <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {completionStatus?.checklist?.is_complete ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span>Checklist complete</span>
                </div>
                {completionStatus?.checklist?.is_complete ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                    {completionStatus.checklist.handled_required}/{completionStatus.checklist.required_items}
                  </Badge>
                ) : (
                  <Button size="sm" variant="destructive" onClick={scrollToChecklist}>
                    <CheckSquare className="h-4 w-4 mr-1" />
                    View Checklist
                  </Button>
                )}
              </div>
            )}

            {/* Item Photos Status */}
            {completionStatus?.item_photos?.require_item_photos && (
              <div className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg">
                <div className="flex items-center gap-2">
                  {completionStatus?.item_photos?.is_complete ? (
                    <CheckCircle className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <span>Item photos</span>
                </div>
                {completionStatus?.item_photos?.is_complete ? (
                  <Badge variant="outline" className="bg-emerald-50 text-emerald-700">
                    {completionStatus.item_photos.items_with_photos}/{completionStatus.item_photos.photo_required_items}
                  </Badge>
                ) : (
                  <Button size="sm" variant="destructive" onClick={scrollToChecklist}>
                    <Camera className="h-4 w-4 mr-1" />
                    Add Photos
                  </Button>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            {completionStatus?.can_complete && (
              <Button 
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                onClick={handleDepart}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Complete & Depart
              </Button>
            )}
            <Button variant="outline" className="w-full" onClick={() => setShowCompletionDialog(false)}>
              Continue Working
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Help Request Dialog */}
      <Dialog open={showHelpDialog} onOpenChange={setShowHelpDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Help</DialogTitle>
            <DialogDescription>
              Your manager will be notified immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="help-message">What do you need help with?</Label>
            <Textarea
              id="help-message"
              value={helpMessage}
              onChange={(e) => setHelpMessage(e.target.value)}
              placeholder="e.g., Need supplies, can't access property, client has special request..."
              className="mt-2"
              rows={4}
              data-testid="help-message-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHelpDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRequestHelp}
              disabled={actionLoading || helpMessage.length < 5}
              data-testid="send-help-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Can't Attend Dialog */}
      <Dialog open={showCantAttendDialog} onOpenChange={setShowCantAttendDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Can't Attend Job
            </DialogTitle>
            <DialogDescription>
              This will notify your manager and the job will need to be reassigned. Please provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="cant-attend-reason">Reason</Label>
            <Textarea
              id="cant-attend-reason"
              value={cantAttendReason}
              onChange={(e) => setCantAttendReason(e.target.value)}
              placeholder="e.g., Vehicle breakdown, illness, emergency..."
              className="mt-2"
              rows={4}
              data-testid="cant-attend-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCantAttendDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCantAttend}
              disabled={actionLoading || cantAttendReason.length < 10}
              data-testid="confirm-cant-attend-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Notify Manager
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Checklist Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Task to Checklist</DialogTitle>
            <DialogDescription>
              Add a task for something the client requested outside the original quote.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="new-task">Task Description</Label>
            <Input
              id="new-task"
              value={newItemTask}
              onChange={(e) => setNewItemTask(e.target.value)}
              placeholder="e.g., Clean inside oven, wash windows..."
              className="mt-2"
              data-testid="new-task-input"
            />
            <p className="text-xs text-zinc-500 mt-2">
              This will be added to your checklist under "Additional Requests"
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddChecklistItem}
              disabled={actionLoading || newItemTask.length < 3}
              data-testid="add-task-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Plus className="mr-2 h-4 w-4" />
              Add Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upsell Note for Taleeia Dialog */}
      <Dialog open={showUpsellDialog} onOpenChange={setShowUpsellDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <DollarSign className="h-5 w-5" />
              Alert Taleeia - Additional Services
            </DialogTitle>
            <DialogDescription>
              Describe what extra services this client might need. Taleeia will contact them to discuss and quote.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="upsell-note">What did you notice?</Label>
            <Textarea
              id="upsell-note"
              value={upsellNote}
              onChange={(e) => setUpsellNote(e.target.value)}
              placeholder="e.g., The oven needs a deep clean. Client mentioned wanting carpets done. Windows are very dirty and could use professional cleaning..."
              className="mt-2"
              rows={4}
              data-testid="upsell-note-input"
            />
            <div className="mt-3 p-3 bg-emerald-50 rounded-lg text-sm text-emerald-800">
              <strong>What happens next:</strong>
              <ul className="mt-1 list-disc list-inside text-emerald-700">
                <li>Taleeia gets notified immediately</li>
                <li>She'll contact {booking?.client?.name || 'the client'}</li>
                <li>If they're interested, she'll prepare a quote</li>
                <li>You might get assigned the extra work!</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpsellDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handleSendUpsellNote}
              disabled={actionLoading || upsellNote.length < 10}
              data-testid="send-upsell-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <Send className="mr-2 h-4 w-4" />
              Send to Taleeia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Staff Notes Dialog */}
      <Dialog open={showStaffNotesDialog} onOpenChange={setShowStaffNotesDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <FileText className="h-5 w-5" />
              Job Notes for Management
            </DialogTitle>
            <DialogDescription>
              Add notes about this job for Taleeia and management. These notes are saved with the job and visible in reports.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="staff-notes">Your Notes</Label>
            <Textarea
              id="staff-notes"
              value={staffNotes}
              onChange={(e) => setStaffNotes(e.target.value)}
              placeholder="e.g., Client was happy with the service. Found a small stain on the couch that couldn't be removed. Toilet flush was already broken when I arrived..."
              className="mt-2"
              rows={5}
              data-testid="staff-notes-input"
            />
            <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <strong>These notes will:</strong>
              <ul className="mt-1 list-disc list-inside text-blue-700">
                <li>Be saved permanently with this job</li>
                <li>Be visible on the booking details</li>
                <li>Notify Taleeia and management</li>
                <li>Help track any issues or special situations</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStaffNotesDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={handleSaveStaffNotes}
              disabled={actionLoading || !staffNotes.trim()}
              data-testid="save-staff-notes-btn"
            >
              {actionLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              <FileText className="mr-2 h-4 w-4" />
              Save Notes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Not Done Reason Dialog */}
      <Dialog open={showNotDoneDialog} onOpenChange={setShowNotDoneDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Mark as Not Done
            </DialogTitle>
            <DialogDescription>
              Please explain why this item couldn't be completed.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {notDoneItem && (
              <div className="mb-4 p-3 bg-zinc-50 rounded-lg">
                <p className="text-sm font-medium text-zinc-700">{notDoneItem.task}</p>
              </div>
            )}
            <Label htmlFor="not-done-reason">Reason</Label>
            <Textarea
              id="not-done-reason"
              value={notDoneReason}
              onChange={(e) => setNotDoneReason(e.target.value)}
              placeholder="e.g., Client requested to skip this area, item was broken, couldn't access..."
              className="mt-2"
              rows={3}
              data-testid="not-done-reason-input"
            />
            <p className="text-xs text-zinc-500 mt-2">
              This reason will be recorded and visible to management.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNotDoneDialog(false)}>
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleSubmitNotDone}
              disabled={!notDoneReason.trim()}
              data-testid="submit-not-done-btn"
            >
              Mark as Not Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Odometer Reading Dialog */}
      <Dialog open={showOdometerDialog} onOpenChange={setShowOdometerDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Gauge className="h-5 w-5 text-primary" />
              {odometerType === 'arrive_job' ? 'Arrival' : 'Departure'} Odometer
            </DialogTitle>
            <DialogDescription>
              Enter your current odometer reading to track kilometers for this job.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {lastOdometerReading && (
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="text-muted-foreground">Last reading:</p>
                <p className="font-semibold text-lg">{lastOdometerReading.reading_km.toLocaleString()} km</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(lastOdometerReading.recorded_at).toLocaleString('en-AU', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="odometer">Current Odometer (km)</Label>
              <div className="relative">
                <Car className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="odometer"
                  type="number"
                  placeholder="e.g., 45678"
                  value={odometerReading}
                  onChange={(e) => setOdometerReading(e.target.value)}
                  className="pl-10 text-lg h-12"
                  autoFocus
                  data-testid="odometer-input"
                />
              </div>
              {lastOdometerReading && odometerReading && parseFloat(odometerReading) > lastOdometerReading.reading_km && (
                <p className="text-sm text-emerald-600">
                  Distance: {(parseFloat(odometerReading) - lastOdometerReading.reading_km).toFixed(1)} km
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="ghost"
              onClick={handleSkipOdometer}
              disabled={actionLoading}
              className="w-full sm:w-auto"
            >
              Skip for now
            </Button>
            <Button
              onClick={handleOdometerSubmit}
              disabled={actionLoading || !odometerReading}
              className="w-full sm:w-auto"
              data-testid="submit-odometer-btn"
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Record & Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        index={lightboxIndex}
        slides={lightboxSlides}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  );
};

export default StaffJobHubPage;
